import { useState, useCallback } from 'react';
import { useClipboard } from '../contexts/ClipboardContext';
import { renameFolderApi, createFolderApi, deleteFolderApi, moveProjectApi, copyProjectApi } from '../services/folder.service';
import type { Folder } from '../services/folder.service';
import { findFolder, findProjectParent } from '../helpers/folder.utils';
import { parseNodeKey } from '../utils/nodeId';
import { deleteProjects, exportProjects } from '../services/project.service';

// Define props the hook needs
interface FolderOperationsProps {
  folders: Folder[]; // Needed for finding parent names/IDs
  refetchFolders: (showLoading?: boolean) => Promise<void>;
  renamingNodeId: string; // Needed for rename submit
  renameValue: string; // Needed for rename submit
  onRenameCancel: () => void; // To cancel rename UI after submit
  setSelectedNodeIds: (ids: string[]) => void; // To clear selection after delete
  selectedNodeIds: string[]; // To check if deleted item was selected
  // Add setters for loaded test cases and clipboard if needed after delete
  clearLoadedTestCasesForProject: (projectIds: number[]) => void;
  refetchAllProjects: (setLoading?: boolean) => Promise<void>; // Add the new prop type
  setExpandedNodeIds: (ids: string[]) => void;
  loadedTestCases: Record<number, any>;
}

export function useFolderOperations({
  folders,
  refetchFolders,
  renamingNodeId,
  renameValue,
  onRenameCancel,
  setSelectedNodeIds,
  selectedNodeIds,
  clearLoadedTestCasesForProject,
  refetchAllProjects, // Add the new prop
  setExpandedNodeIds,
  loadedTestCases,
}: FolderOperationsProps) {
  const { mode, items, copy, cut, clear: clearClipboard } = useClipboard();
  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false);
  const [newFolderParentId, setNewFolderParentId] = useState<number | null | undefined>(undefined);

  // --- Create Folder Logic ---
  const openCreateFolderModal = useCallback((nodeId: string) => {
    let parentId: number | null = null;
    if (nodeId !== 'folder-root') {
      try {
          parentId = parseNodeKey(nodeId).id;
      } catch (e) {
          console.error(`Error parsing parent node ID for new folder: ${nodeId}`, e);
          // Decide how to handle - maybe default to root or show error?
          parentId = 0; // Defaulting to root for now
      }
    }
    setNewFolderParentId(parentId);
    setIsCreatingFolder(true);
  }, []); // No dependencies needed

  const handleCreateCancel = useCallback(() => {
    setIsCreatingFolder(false);
    setNewFolderParentId(undefined);
  }, []);

  const handleCreateSubmit = useCallback(async (name: string) => {
    if (newFolderParentId === undefined) {
      console.error("Cannot create folder without a parent ID context.");
      return;
    }
    setIsCreatingFolder(false);
    const parentId = newFolderParentId;
    setNewFolderParentId(undefined);

    try {
      await createFolderApi(name, parentId);
      await refetchFolders(false);
      // TODO: Optionally find the new folder and select it or expand the parent
    } catch (error) {
      console.error('Failed to create folder:', error);
      // TODO: Show error to user (e.g., Snackbar)
      // Safely access error message
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to create folder: ${message}`);
    }
  }, [newFolderParentId, refetchFolders]);

  const getParentFolderName = useCallback((parentId: number | null): string | undefined => {
    if (parentId === null || parentId === 0) return undefined; // Root
    const parentFolder = findFolder(folders, parentId);
    return parentFolder?.name;
  }, [folders]); // Depends on current folder structure

  // --- Rename Folder Logic ---
  const handleRenameSubmit = useCallback(async () => {
    if (!renamingNodeId) return;
    let id: number;
    try {
        id = parseNodeKey(renamingNodeId).id;
    } catch(e) {
        console.error(`Error parsing node ID for rename submit: ${renamingNodeId}`, e);
        onRenameCancel(); // Cancel the rename UI
        return;
    }
    const submittedName = renameValue.trim(); // Trim whitespace
    if (!submittedName) {
        alert("Folder name cannot be empty."); // Basic validation
        return; // Don't submit empty names
    }
    onRenameCancel(); // Optimistically close rename UI

    try {
      await renameFolderApi(id, submittedName);
      await refetchFolders(false);
    } catch (error) {
      console.error('Rename failed', error);
      // Safely access error message
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to rename folder: ${message}`);
      // Maybe re-open rename UI or show persistent error?
    }
  }, [renamingNodeId, renameValue, onRenameCancel, refetchFolders]);


  // --- Delete Folder Logic ---
  const handleDeleteFolder = useCallback(async (nodeId: string) => {
    let folderId: number;
     try {
        folderId = parseNodeKey(nodeId).id;
    } catch(e) {
        console.error(`Error parsing node ID for delete folder: ${nodeId}`, e);
        return;
    }
    const folderToDelete = findFolder(folders, folderId);
    const folderName = folderToDelete ? folderToDelete.name : 'this folder';

    if (window.confirm(`Are you sure you want to delete '${folderName}'? This action cannot be undone.`)) {
      try {
        await deleteFolderApi(folderId);
        await refetchFolders(false);
        // If the deleted folder was selected, clear the selection
        if (selectedNodeIds.includes(nodeId)) {
          setSelectedNodeIds([]); // Clear selection entirely or filter out the specific ID
          // Also clear details panel if needed (handled in Folders.tsx or useFolderSelectionDetails)
        }
      } catch (error: any) {
        console.error('Failed to delete folder:', error);
        let message = 'Could not delete folder. An unexpected error occurred.';
        // Check specific properties common in API errors
        if (error?.response?.status === 400) {
            const apiMessage = error.response?.data?.message || error.response?.data?.detail;
            message = `Could not delete folder: ${apiMessage || 'Folder might not be empty or is protected.'}`;
        } else if (error?.response?.status === 404) {
            message = 'Could not delete folder: Folder not found.';
        } else if (error instanceof Error) {
            // Fallback to standard Error message if specific checks fail
            message = `Could not delete folder: ${error.message}`;
        }
        alert(message); // Replace with a proper UI notification
      }
    }
  }, [folders, refetchFolders, selectedNodeIds, setSelectedNodeIds]); // Depends on folder data and selection state

  // --- Clipboard Actions ---
  const handleCopyProjects = useCallback((projectIds: number[]) => {
    copy(projectIds.map((id) => ({ kind: 'project', id })));
  }, [copy]);

  const handleCutProjects = useCallback((projectIds: number[], _srcFolderId?: number) => {
    // srcFolderId might be useful if API requires it for cutting, but clipboard only stores item kind/id
    cut(projectIds.map((id) => ({ kind: 'project', id })));
  }, [cut]);

  const handlePasteItems = useCallback(async (destNodeId: string) => {
    if (!mode || items.length === 0) return;
    let destFolderId: number;
    try {
        destFolderId = destNodeId === 'folder-root' ? 0 : parseNodeKey(destNodeId).id;
    } catch(e) {
        console.error(`Error parsing destination node ID for paste: ${destNodeId}`, e);
        alert(`Error: Invalid destination folder selected for paste.`);
        return;
    }

    // Indicate processing started (e.g., show spinner)

    try {
      if (mode === 'copy') {
        // Use Promise.all for potentially faster copying if API supports concurrent requests
        await Promise.all(items.filter(i => i.kind === 'project').map(item =>
          copyProjectApi(item.id, destFolderId)
        ));
        // TODO: Handle folder/testcase copy if needed
      } else if (mode === 'cut') {
        await Promise.all(items.filter(i => i.kind === 'project').map(item => {
          const srcFolderId = findProjectParent(folders, item.id) ?? 0; // Find source folder
          return moveProjectApi(item.id, srcFolderId, destFolderId);
        }));
         // TODO: Handle folder/testcase move if needed
      }
      clearClipboard(); // Clear clipboard only on successful paste
      await refetchFolders(false);
    } catch (error) {
      console.error('Failed to paste items:', error);
      // TODO: Provide more specific error messages based on API response if possible
      // Safely access error message
      const message = error instanceof Error ? error.message : 'An error occurred.';
      alert(`Failed to paste: ${message}`);
    } finally {
      // Indicate processing finished (e.g., hide spinner)
    }
  }, [mode, items, folders, clearClipboard, refetchFolders]); // Depends on clipboard state and folder data


  // --- Delete Projects ---
  const handleDeleteProjects = useCallback(async (projectIds: number[]) => {
    if (projectIds.length === 0) return;
    const message = projectIds.length > 1
      ? `Are you sure you want to delete these ${projectIds.length} projects? This action cannot be undone.`
      : 'Are you sure you want to delete this project? This action cannot be undone.';

    if (!window.confirm(message)) return;

    try {
      await deleteProjects(projectIds);
      await refetchFolders(false);
      await refetchAllProjects(false); // Call the refetch function here

      // Clear selection if any deleted projects (or their test cases) were selected
      const remainingSelected = selectedNodeIds.filter(id => {
          try {
              const { type, id: selectedId } = parseNodeKey(id);
              if (type === 'project') {
                  return !projectIds.includes(selectedId);
              }
              // If we could determine the parent project ID for a test case here,
              // we could also filter out selected test cases whose parent project was deleted.
              // This requires enhancing how selection state is managed or passing parent info.
              // For now, we only clear selected projects.
              return true; // Keep non-project selections
          } catch {
              return true; // Keep invalid/unparseable IDs? Or filter them?
          }
      });
      setSelectedNodeIds(remainingSelected);

      // Clear clipboard state if deleted items were cut/copied
      if (mode === 'copy' || mode === 'cut') {
          const remainingItems = items.filter(item =>
              item.kind !== 'project' || !projectIds.includes(item.id)
          );
          if (remainingItems.length < items.length) {
              if (remainingItems.length === 0) {
                  clearClipboard();
              } else if (mode === 'copy') {
                  copy(remainingItems);
              } else {
                  cut(remainingItems);
              }
          }
      }

      // Clear any loaded test cases for the deleted projects
      clearLoadedTestCasesForProject(projectIds);

    } catch (error) {
      console.error('Failed to delete project(s):', error);
      // Safely access error message
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to delete project(s): ${message}`);
    }
  }, [selectedNodeIds, refetchFolders, refetchAllProjects, setSelectedNodeIds, mode, items, copy, cut, clearClipboard, clearLoadedTestCasesForProject]); // Add refetchAllProjects to dependencies

  // --- Export Projects ---
  const handleExportProjects = useCallback(async (projectIds: number[]) => {
    if (projectIds.length === 0) return;
    await exportProjects(projectIds);
  }, []);


  // --- Refresh All ---
  const handleRefreshAll = useCallback(async () => {
    // Preserve current selection to restore after reload
    const prevSelection = [...selectedNodeIds];

    // Clear any stored state so the tree collapses like a fresh load
    sessionStorage.removeItem('folderViewSessionState');
    sessionStorage.removeItem('folderViewTestCaseState');

    // Reset local state
    setSelectedNodeIds([]);
    setExpandedNodeIds(['folder-root']);
    clearClipboard();
    clearLoadedTestCasesForProject(Object.keys(loadedTestCases).map(Number));

    // Refetch folders and projects
    await refetchFolders(true);
    await refetchAllProjects(true);

    // Restore selection so active item stays highlighted
    if (prevSelection.length > 0) {
      setSelectedNodeIds(prevSelection);
    }
  }, [
    selectedNodeIds,
    refetchFolders,
    refetchAllProjects,
    setSelectedNodeIds,
    setExpandedNodeIds,
    clearClipboard,
    clearLoadedTestCasesForProject,
    loadedTestCases,
  ]);


  return {
    // Create Folder State & Handlers
    isCreatingFolder,
    openCreateFolderModal, // Renamed from handleNewFolder
    handleCreateCancel,
    handleCreateSubmit,
    getParentFolderName,
    // Rename Folder Submit Handler
    handleRenameSubmit,
    // Delete Folder Handler
    handleDeleteFolder,
    // Clipboard Handlers
    handleCopyProjects,
    handleCutProjects,
    handlePasteItems, // Renamed from handlePasteProject
    // Export Projects Handler
    handleExportProjects,
    // Delete Projects Handler
    handleDeleteProjects,
    // Refresh Handler
    handleRefreshAll,
    // Clipboard state derived for Menu (can be derived in Folders.tsx too)
    projectClipboardItems: items.filter((i) => i.kind === 'project'),
    clipboardMode: mode,
    newFolderParentId, // Return the state for the modal
  };
} 
